// FICHA 360 DEL PROVEEDOR (pedido de Rodrigo, 2026-10-20: «estoy en Compras › Proveedores, me voy a
// la ficha y me tira a temas comerciales de venta»). Desde Compras, el proveedor se ve como proveedor:
// lo que le debemos (y lo retenido esperando NC), sus facturas, las guías que todavía no facturó, sus
// órdenes de compra, los pagos y cómo cobra el flete. Las acciones son de compras: nueva OC, guía de
// recepción, registrar factura y pagar. «Ficha del proveedor» es la mantención de sus datos.
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../api/cliente';
import { useFichaCliente } from '../api/consultas';
import { FichaMaestro } from '../clientes/FichaMaestro';
import { EditarFicha } from '../clientes/EditarFicha';
import { PanelError } from '../comun/Estados';
import { fecha, NOMBRE_CONDICION, pesos, rut } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import {
  NOMBRE_ESTADO_PAGO,
  useDocumentosCxp,
  usePagos,
  type DocumentoProveedor,
} from '../tesoreria/datosPagos';
import { CostosLogisticos } from './CostosLogisticos';
import {
  NOMBRE_ESTADO_DOC,
  NOMBRE_ESTADO_OC,
  NOMBRE_TIPO_COMPRA,
  useFacturasCompra,
  useOrdenesCompra,
  type EstadoOrden,
} from './datos';

type Pestana = 'pagar' | 'facturas' | 'guias' | 'ordenes' | 'pagos' | 'flete';

const NOMBRE_ESTRATEGIA: Record<string, string> = {
  FLETE_POR_VALOR: 'flete total por valor',
  FLETE_POR_UNIDADES: 'flete total por unidades',
  FLETE_POR_COSTO_LOGISTICO: 'flete total por costo logístico',
  FLETE_DIRECTO: 'flete en la línea',
};
const TD: Record<string, string> = { FACTURA: 'FCC', NOTA_CREDITO: 'NCC', NOTA_DEBITO: 'NDC' };
const OC_ABIERTA: EstadoOrden[] = ['BORRADOR', 'EN_APROBACION', 'APROBADA', 'EMITIDA'];

interface GuiaPorFacturar {
  recepcionId: string;
  numeroVisible: string;
  fecha: string;
  guia: string | null;
  lineas: Array<{ pendiente: string; precioOc: string | null; descripcion: string }>;
}

export function ContextoProveedor({
  entidadId,
  alAbrirCompra,
  alPagar,
}: {
  entidadId: string;
  /** Abre un documento de Compras (existente o nuevo) en su pestaña. */
  alAbrirCompra: (params: Record<string, string>, titulo: string) => void;
  alPagar: (p: { id: string; razonSocial: string }) => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const ficha = useFichaCliente(empresaId, entidadId);
  const [vista, setVista] = useState<'resumen' | 'ficha'>('resumen');
  const [pestana, setPestana] = useState<Pestana>('pagar');
  const [editando, setEditando] = useState(false);

  const cxp = useDocumentosCxp(empresaId, { proveedorEntidadId: entidadId, abiertos: true });
  const facturas = useFacturasCompra(empresaId, { proveedorEntidadId: entidadId });
  const ordenes = useOrdenesCompra(empresaId, { proveedorEntidadId: entidadId });
  const pagos = usePagos(empresaId, { proveedorEntidadId: entidadId });
  const guias = useQuery({
    queryKey: ['emp', empresaId, 'compras', 'por-facturar', entidadId],
    queryFn: () =>
      api.get<GuiaPorFacturar[]>('/compras/recepciones/por-facturar', {
        query: { empresaId, proveedorEntidadId: entidadId },
      }),
  });
  const estrategia = useQuery({
    queryKey: ['emp', empresaId, 'compras', 'estrategia-flete', entidadId],
    queryFn: () =>
      api.get<{ estrategia: { estrategia: string } | null }>(
        '/compras/costos-logisticos/estrategia',
        { query: { empresaId, proveedorEntidadId: entidadId } },
      ),
  });

  const docs = cxp.data ?? [];
  const kpi = useMemo(() => {
    const saldo = docs.reduce((s, d) => s + Number(d.saldo), 0);
    const vencido = docs.filter((d) => d.diasVencido > 0).reduce((s, d) => s + Number(d.saldo), 0);
    const retenido = docs.reduce((s, d) => s + Number(d.retenido ?? 0), 0);
    const hace90 = new Date(Date.now() - 90 * 86_400_000).toISOString().slice(0, 10);
    const lista = facturas.data?.items ?? [];
    const compras90 = lista
      .filter((f) => f.estado === 'ACEPTADA' && f.fecha_emision.slice(0, 10) >= hace90)
      .reduce((s, f) => s + Number(f.monto_total), 0);
    const ultima =
      lista
        .map((f) => f.fecha_emision.slice(0, 10))
        .sort()
        .pop() ?? null;
    const ocAbiertas = (ordenes.data?.items ?? []).filter((o) =>
      OC_ABIERTA.includes(o.estado),
    ).length;
    return { saldo, vencido, retenido, compras90, ultima, ocAbiertas };
  }, [docs, facturas.data, ordenes.data]);

  if (ficha.isPending) return <p className="apagado pantalla">Cargando proveedor…</p>;
  if (ficha.isError) return <PanelError error={ficha.error} />;
  const f = ficha.data;
  const e = f.entidad;
  const rel = f.empresas.find((x) => x.empresa_id === empresaId) ?? null;
  const prov = { id: e.id, razonSocial: e.razon_social };
  const abrirNuevo = (doc: 'oc' | 'recepcion' | 'factura', titulo: string, extra = {}) =>
    alAbrirCompra({ doc, proveedorId: e.id, ...extra }, titulo);
  const nGuias = guias.data?.length ?? 0;
  const pestanas: Array<[Pestana, string]> = [
    ['pagar', `Por pagar (${docs.length})`],
    ['facturas', 'Facturas'],
    ['guias', `Guías sin factura (${nGuias})`],
    ['ordenes', `Órdenes de compra (${kpi.ocAbiertas} abiertas)`],
    ['pagos', 'Pagos'],
    ['flete', 'Flete y costos logísticos'],
  ];

  return (
    <div
      className="pantalla contexto-cliente contexto-proveedor"
      data-pantalla="contexto-proveedor"
    >
      <div className="barra-contexto">
        <span className="avatar-entidad" aria-hidden>
          {e.razon_social.slice(0, 2).toUpperCase()}
        </span>
        <div className="titulo-entidad">
          <h2>{e.razon_social}</h2>
          <span className="mono apagado">
            {rut(e.rut, e.dv)}
            {e.giro ? ` · ${e.giro}` : ''}
          </span>
        </div>
        <span className="insignia tono-info">
          {rel?.es_cliente !== false && rel?.es_proveedor ? 'Cliente y proveedor' : 'Proveedor'}
        </span>
        {rel && (
          <span className="insignia tono-neutro">
            {NOMBRE_CONDICION[rel.condicion_pago] ?? rel.condicion_pago}
            {rel.plazo_dias ? ` ${rel.plazo_dias} días` : ''}
          </span>
        )}
        <span
          className="insignia tono-neutro"
          title="Cómo cobra el flete (Flete y costos logísticos)"
        >
          {NOMBRE_ESTRATEGIA[estrategia.data?.estrategia?.estrategia ?? ''] ?? 'flete en la línea'}
        </span>
        {e.estado !== 'activa' && <span className="insignia tono-malo">{e.estado}</span>}
        <span className="espaciador" />
        {puede('compras.oc.crear') && (
          <button type="button" onClick={() => abrirNuevo('oc', `OC nueva · ${e.razon_social}`)}>
            Nueva OC
          </button>
        )}
        {puede('compras.recepcion.crear') && (
          <button type="button" onClick={() => abrirNuevo('recepcion', `Guía · ${e.razon_social}`)}>
            Guía de recepción
          </button>
        )}
        {puede('compras.factura.crear') && (
          <button
            type="button"
            onClick={() => abrirNuevo('factura', `Factura · ${e.razon_social}`)}
          >
            Registrar factura
          </button>
        )}
        {puede('tesoreria.pago.solicitar') && (
          <button type="button" className="primario" onClick={() => alPagar(prov)}>
            Pagar
          </button>
        )}
      </div>

      {/* indicadores del proveedor */}
      <section className="situacion kpis-proveedor" aria-label="Situación con el proveedor">
        <div className="b">
          <span className="etiqueta">Le debemos</span>
          <span className="v">{pesos(String(kpi.saldo))}</span>
        </div>
        <div className={`b ${kpi.vencido > 0 ? 'mal' : ''}`}>
          <span className="etiqueta">Vencido</span>
          <span className="v">{pesos(String(kpi.vencido))}</span>
        </div>
        <div className="b" title="Reclamado al proveedor: se paga con su nota de crédito">
          <span className="etiqueta">Retenido (espera NC)</span>
          <span className="v">{kpi.retenido ? pesos(String(kpi.retenido)) : '—'}</span>
        </div>
        <div className="b">
          <span className="etiqueta">Guías sin factura</span>
          <span className="v">{nGuias}</span>
        </div>
        <div className="b">
          <span className="etiqueta">OC abiertas</span>
          <span className="v">{kpi.ocAbiertas}</span>
        </div>
        <div className="b">
          <span className="etiqueta">Compras 90 días</span>
          <span className="v">{pesos(String(kpi.compras90))}</span>
        </div>
        <div className="b">
          <span className="etiqueta">Última compra</span>
          <span className="v">{kpi.ultima ? fecha(kpi.ultima) : '—'}</span>
        </div>
      </section>

      <div className="segmentos vista-cliente" role="radiogroup" aria-label="Vista del proveedor">
        {(
          [
            ['resumen', 'Resumen'],
            ['ficha', 'Ficha del proveedor'],
          ] as const
        ).map(([id, n]) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={vista === id}
            className={vista === id ? 'activo' : ''}
            onClick={() => setVista(id)}
          >
            {n}
          </button>
        ))}
      </div>

      {vista === 'ficha' ? (
        <FichaMaestro
          key={`${e.id}-${rel?.version ?? 0}`}
          modo="proveedor"
          empresaId={empresaId}
          ficha={f}
          vendedores={[]}
          listas={[]}
          alSolicitarCambio={() => setEditando(true)}
        />
      ) : (
        <section className="resumen-proveedor" aria-label="Resumen del proveedor">
          <div className="secciones-od" role="tablist" aria-label="Resumen del proveedor">
            {pestanas.map(([id, n]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={pestana === id}
                className={pestana === id ? 'activa' : ''}
                onClick={() => setPestana(id)}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="cuerpo-resumen">
            {pestana === 'pagar' && (
              <PorPagar
                docs={docs}
                cargando={cxp.isLoading}
                alAbrir={(d) =>
                  alAbrirCompra({ doc: 'factura', id: d.documentoId }, `FCC ${d.folio ?? ''}`)
                }
              />
            )}
            {pestana === 'facturas' && (
              <Tabla
                vacio="Todavía no hay facturas de este proveedor."
                cargando={facturas.isLoading}
                columnas={['Tipo', 'Folio', 'Emisión', 'Estado', 'Total']}
                filas={(facturas.data?.items ?? []).map((x) => ({
                  clave: x.id,
                  alAbrir: () =>
                    alAbrirCompra(
                      {
                        doc:
                          x.clase_documento === 'FACTURA' || !x.clase_documento
                            ? 'factura'
                            : 'nota',
                        id: x.id,
                      },
                      `${NOMBRE_TIPO_COMPRA[x.tipo_sii ?? 33] ?? 'FCC'} ${x.folio ?? ''}`,
                    ),
                  celdas: [
                    NOMBRE_TIPO_COMPRA[x.tipo_sii ?? 33] ?? x.tipo_sii ?? '',
                    <span className="mono">{x.folio ?? '—'}</span>,
                    fecha(x.fecha_emision),
                    x.invalidada ? 'Anulado' : (NOMBRE_ESTADO_DOC[x.estado] ?? x.estado),
                    pesos(x.monto_total),
                  ],
                }))}
              />
            )}
            {pestana === 'guias' && (
              <Tabla
                vacio="No hay guías recibidas pendientes de facturar."
                cargando={guias.isLoading}
                columnas={['Recepción', 'Fecha', 'Guía', 'Productos', 'Estimado (precio OC)', '']}
                filas={(guias.data ?? []).map((g) => ({
                  clave: g.recepcionId,
                  alAbrir: () =>
                    alAbrirCompra({ doc: 'recepcion', id: g.recepcionId }, g.numeroVisible),
                  celdas: [
                    <span className="mono">{g.numeroVisible}</span>,
                    fecha(g.fecha),
                    g.guia ?? '—',
                    g.lineas.length,
                    (() => {
                      const t = g.lineas.reduce(
                        (s, l) => s + (l.precioOc ? Number(l.pendiente) * Number(l.precioOc) : 0),
                        0,
                      );
                      return t ? pesos(String(Math.round(t))) : '—';
                    })(),
                    puede('compras.factura.crear') ? (
                      <button
                        type="button"
                        className="boton-mini"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          abrirNuevo('factura', `Factura · ${e.razon_social}`, {
                            recepcionId: g.recepcionId,
                          });
                        }}
                      >
                        Registrar su factura
                      </button>
                    ) : (
                      ''
                    ),
                  ],
                }))}
              />
            )}
            {pestana === 'ordenes' && (
              <Tabla
                vacio="No hay órdenes de compra a este proveedor."
                cargando={ordenes.isLoading}
                columnas={['N° OC', 'Fecha', 'Entrega', 'Estado', 'Neto']}
                filas={(ordenes.data?.items ?? []).map((o) => ({
                  clave: o.id,
                  alAbrir: () => alAbrirCompra({ doc: 'oc', id: o.id }, o.numero_visible),
                  celdas: [
                    <span className="mono">{o.numero_visible}</span>,
                    fecha(o.fecha_negocio),
                    o.fecha_entrega_esperada ? fecha(o.fecha_entrega_esperada) : '—',
                    NOMBRE_ESTADO_OC[o.estado] ?? o.estado,
                    pesos(o.total_neto),
                  ],
                }))}
              />
            )}
            {pestana === 'pagos' && (
              <Tabla
                vacio="Todavía no hay pagos a este proveedor."
                cargando={pagos.isLoading}
                columnas={['N°', 'Fecha', 'Medio', 'Estado', 'Total']}
                filas={(pagos.data ?? []).map((p) => ({
                  clave: p.pagoId,
                  celdas: [
                    <span className="mono">{p.numero}</span>,
                    fecha(p.pagadoEn ?? p.fechaProgramada),
                    p.medio.toLowerCase().replace('_', ' '),
                    NOMBRE_ESTADO_PAGO[p.estado] ?? p.estado,
                    pesos(p.total),
                  ],
                }))}
              />
            )}
            {pestana === 'flete' && (
              <CostosLogisticos proveedorFijo={{ id: e.id, rut: e.rut, razon: e.razon_social }} />
            )}
          </div>
        </section>
      )}
      {editando && (
        <EditarFicha
          empresaId={empresaId}
          ficha={f}
          vendedores={[]}
          listas={[]}
          pendientes={new Set()}
          alCerrar={() => setEditando(false)}
          alListo={() => setEditando(false)}
        />
      )}
    </div>
  );
}

function PorPagar({
  docs,
  cargando,
  alAbrir,
}: {
  docs: DocumentoProveedor[];
  cargando: boolean;
  alAbrir: (d: DocumentoProveedor) => void;
}) {
  return (
    <Tabla
      vacio="No le debemos nada a este proveedor."
      cargando={cargando}
      columnas={[
        'TD',
        'Folio',
        'Emisión',
        'Vence',
        'Días',
        'Total',
        'NC',
        'Pagado',
        'Retenido',
        'Saldo',
      ]}
      numericas={[4, 5, 6, 7, 8, 9]}
      filas={docs.map((d) => ({
        clave: d.documentoId,
        clase: d.diasVencido > 0 ? 'vencido' : '',
        alAbrir: d.clase === 'FACTURA' ? () => alAbrir(d) : undefined,
        celdas: [
          TD[d.clase] ?? d.clase,
          <span className="mono">{d.folio ?? '—'}</span>,
          fecha(d.fechaEmision),
          fecha(d.fechaVencimiento),
          d.diasVencido || '',
          pesos(d.montoTotal),
          Number(d.notasCredito) ? pesos(d.notasCredito) : '',
          Number(d.pagado) ? pesos(d.pagado) : '',
          Number(d.retenido ?? 0) ? pesos(d.retenido!) : '',
          <b>{pesos(d.saldo)}</b>,
        ],
      }))}
    />
  );
}

function Tabla({
  columnas,
  filas,
  vacio,
  cargando,
  numericas,
}: {
  columnas: string[];
  filas: Array<{
    clave: string;
    celdas: React.ReactNode[];
    alAbrir?: (() => void) | undefined;
    clase?: string;
  }>;
  vacio: string;
  cargando: boolean;
  numericas?: number[];
}) {
  // Por defecto, la última columna es un monto.
  const der = new Set(numericas ?? [columnas.length - 1]);
  if (cargando) return <p className="apagado">Cargando…</p>;
  if (!filas.length) return <p className="apagado vacio-resumen">{vacio}</p>;
  return (
    <table className="grilla compacta grilla-resumen">
      <thead>
        <tr>
          {columnas.map((c, i) => (
            <th key={i} className={der.has(i) ? 'der' : ''}>
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {filas.map((f) => (
          <tr
            key={f.clave}
            className={`${f.clase ?? ''} ${f.alAbrir ? 'abrible' : ''}`}
            onDoubleClick={f.alAbrir}
            title={f.alAbrir ? 'Doble clic para abrir' : undefined}
          >
            {f.celdas.map((c, i) => (
              <td key={i} className={der.has(i) ? 'der' : ''}>
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
